import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  KeyboardEvent,
  FC,
} from 'react';
import { Modal, Spin, UploadFile } from 'antd';
import styles from './CreateProductModal.module.scss';
import ValueChainService from 'services/ValueChainService/valuechain.service';
import type { RcFile } from 'antd/es/upload';
import {
  FETCH_NEW_PRODUCT_LOADER,
  PF_NOT_FOUND_ERROR,
  defaultFileProps,
  ml,
  temperatureControl,
} from './CreateProductModalConstants';
import { validationSchema } from './CreateProductModalConstants';

import { CreateProductModalProps } from 'enums/CreateProductModalEnums';
import { BrandsList } from 'models/BrandsList/BrandsList.model';
import { ProcessingFacility } from 'models/ProcessingFacility/ProcessingFacility.model';
import { getCreateProductModalFields } from './CreateProductModalFields';
import { generatePath, useNavigate } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { HelpDeskTooltipContext } from 'context/HelpDeskTooltipContext';
import isProductDisabled from 'shared/utils/isProductDisabled';
import useRedirect from 'shared/utils/useRedirect';
import CreateProductsService from 'services/CreateProduct/createProduct.service';
import { MetaData } from 'models/MetaData/metaData.model';
import {
  ELEMENT_INPUT,
  KEYDOWN_ENTER,
  defaultYearTab,
} from 'constants/constants';
import { modalCloseIcon } from '../OrganizationModal';
import { validateProductModal } from './productModalValidate';
import ProductModalForm, {
  CategoryOptions,
  CreateProductModalType,
} from './ProductModalForm';
import LocationService from 'services/ActivityDataEntryService/location.service';
import BrandService from 'services/ProductsService/brand.service';
import ProductCreationService from 'services/ProductsService/productCreation.service';
import { convertToOptions } from './createProduct.util';
import { handleFormSubmission } from './createProductUtil';

export interface ProductModalFormValue {
  productName: string;
  name: string;
  description: string;
  brand_id: number | undefined;
  brand: number | undefined;
  productNetWeight: number | null;
  ml: string;
  productGrossWeight: number | null;
  temperatureControl: string | undefined;
  productDensity: number | null;
  processingFacility: number | undefined;
  reporting_period_units: number | null;
  reportingYear: number | null;
  reportingPeriodProduction: number | null;
  percentageProcessingFacilityCostGood: number | null;
  id: string;
  image: string;
  unique_id: string | null;
}

const CreateProductModal: FC<CreateProductModalType> = ({
  showModalState,
  handleModalCancel,
  handleModalConfirm,
  callbackAfterSubmit,
  parentProductId,
  heading,
}: CreateProductModalType) => {
  const {
    createProduct,
    buttonLoading,
    updateProduct,
    fetchSingleProduct,
    newProduct,
    setNewProduct,
    productLoading,
    productImage,
    uploadProductImage,
  } = ProductCreationService();
  const { createBrand, fetchBrands, brandsList } = BrandService();
  const { redirectToActivityDataEntry } = useRedirect();
  const { getFieldsList, getProductsMeta, fieldsMap, products, loading } =
    CreateProductsService();
  const {
    loading: locationLoading,
    fetchLocation,
    location,
  } = LocationService();
  const { redirectToAddLocations } = useRedirect();
  const { fetchProcessingFacilities, processingFacilityList } =
    ValueChainService();
  const navigate = useNavigate();
  const [brandOptions, setBrandOptions] = useState<CategoryOptions[]>([]);
  const [createBrandModalState, setCreateBrandModalState] =
    useState<boolean>(false);
  const [isValidationVisible, setIsValidationVisible] =
    useState<boolean>(false);
  const [isNewProduct, setIsNewProduct] = useState<boolean>(true);
  const [processingFacilityOptions, setProcessingFacilityOptions] = useState<
    CategoryOptions[]
  >([]);
  const [image, setImage] = useState<RcFile>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const handleOpenValidationModal = (errorCode?: string) => {
    if (errorCode === PF_NOT_FOUND_ERROR) setIsValidationVisible(true);
  };

  const handleFormSubmissionWrapper = async (values: ProductModalFormValue) => {
    await handleFormSubmission(values, {
      isNewProduct,
      newProduct,
      products,
      updateProduct,
      createProduct,
      uploadProductImage,
      callbackAfterSubmit,
      handleModalCancel,
      navigate,
      image,
      handleOpenValidationModal,
    });
  };

  const handleOpenCreateBrandModal = () => setCreateBrandModalState(true);

  const handleCloseCreateBrandModal = () => setCreateBrandModalState(false);

  const toggleValidationModal = () => setIsValidationVisible(prev => !prev);

  const { openTooltipModal } = useContext(HelpDeskTooltipContext);

  const handleRedirectToLocations = () =>
    redirectToAddLocations({ isProcessingFacility: true });

  useEffect(() => {
    getFieldsList();
    getProductsMeta();
    fetchBrands({ size: 250 });
    fetchProcessingFacilities({ size: 250 });
  }, []);

  useEffect(() => {
    if (parentProductId && showModalState) {
      const parent = products?.find(item => item.uniqueId === parentProductId);
      if (!parent?.id) return;
      getSingleProduct(parent?.id);
    }
  }, [parentProductId, products, showModalState]);

  useEffect(() => {
    const options = convertToOptions(brandsList);
    const facilityOptions = convertToOptions(processingFacilityList);
    setBrandOptions(options);
    setProcessingFacilityOptions(facilityOptions);
  }, [brandsList, processingFacilityList]);

  const getLocations = async (id?: number) => id && (await fetchLocation(id));

  const createProductForm = localStorage.getItem('create-product');
  let parsedForm: any;
  if (createProductForm) {
    parsedForm = JSON.parse(createProductForm);
  }

  const isDisabled = isProductDisabled(parsedForm?.status);

  const productsOptions = convertToOptions(products);

  const initialFormValues = useMemo(() => {
    const {
      id,
      productName,
      description,
      brand,
      productNetWeight,
      ml,
      productGrossWeight,
      temperatureControl,
      reportingPeriodProduction,
      percentageProcessingFacilityCostGood,
      reportingYear,
      processingFacility,
      productDensity,
    } = parsedForm || {};

    const parentProdData = JSON.parse(JSON.stringify(newProduct ?? '{}'));

    const {
      id: newProductId,
      description: newProductDescription,
      brandId,
      netWeight,
      netWeightUnit,
      grossWeight,
      temperatureControl: newTemperatureControl,
      reportingPeriodUnits,
      processingFacilityCostPercentage,
      reportingYear: newReportingYear,
      processingFacilityId,
      density,
      image,
    } = parentProdData || {};
    if (image) {
      setFileList([{ ...defaultFileProps, url: image }]);
      productImage && setImage(productImage);
    }

    if (processingFacilityId) getLocations(processingFacilityId);

    return {
      image: '',
      id,
      productName: productName ?? newProductId ?? undefined,
      description: description ?? newProductDescription ?? '',
      brand: brand ?? brandId ?? undefined,
      productNetWeight: productNetWeight ?? netWeight ?? null,
      ml: ml ?? netWeightUnit ?? undefined,
      productGrossWeight: productGrossWeight ?? grossWeight ?? null,
      temperatureControl:
        temperatureControl ?? newTemperatureControl ?? undefined,
      reportingPeriodProduction:
        reportingPeriodProduction ?? reportingPeriodUnits ?? null,
      percentageProcessingFacilityCostGood:
        percentageProcessingFacilityCostGood ??
        processingFacilityCostPercentage ??
        null,
      reportingYear: reportingYear ?? newReportingYear ?? undefined,
      processingFacility:
        processingFacility ?? processingFacilityId ?? undefined,
      productDensity: productDensity ?? density ?? null,
      name: parsedForm?.name ?? parentProdData?.name ?? '',
      brand_id: parsedForm?.brand_id ?? parentProdData?.brand_id ?? undefined,
      reporting_period_units:
        parsedForm?.reporting_period_units ??
        parentProdData?.reporting_period_units ??
        null,
      unique_id: parsedForm?.unique_id ?? parentProdData?.unique_id ?? null,
    };
  }, [newProduct, parsedForm, showModalState]);

  const isNotDuplicateProduct = (value: string): number => {
    if (!products.length) return 0;
    const product = products?.find(item => String(item.id) == value);
    return product?.id ?? 0;
  };

  const getSingleProduct = async (id: number) => {
    await fetchSingleProduct(id, true);
    setIsNewProduct(false);
  };

  const handleChangeProductName = async (value: string) => {
    if (value) {
      const duplicateProduct = isNotDuplicateProduct(value);
      if (!!duplicateProduct) await getSingleProduct(duplicateProduct);
    } else if (!isNewProduct) {
      setIsNewProduct(true);
      setNewProduct(undefined);
      setFileList([]);
    }
  };

  const handleSelect = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === KEYDOWN_ENTER) {
      const inputElement = event.currentTarget.querySelector(
        ELEMENT_INPUT
      ) as HTMLInputElement;
      const enteredValue = inputElement?.value?.trim();
      if (!enteredValue) return;
      const duplicateProduct = isNotDuplicateProduct(enteredValue);
      if (!!duplicateProduct) await getSingleProduct(duplicateProduct);
      else setIsNewProduct(true);
    }
  };

  const handleChangePF = async (value: string) => {
    if (value) await getLocations(Number(value));
  };

  const { fields, processingFacilityFields } = useMemo(() => {
    return getCreateProductModalFields({
      brandOptions,
      ml,
      temperatureControl,
      processingFacilityOptions,
      handleOpenCreateBrandModal,
      handleRedirectToLocations,
      openHelpDeskTooltip: openTooltipModal,
      disabled: isDisabled,
      ...((!isNewProduct || parsedForm?.uniqueId) && { fieldsMap: fieldsMap }),
      products: productsOptions,
      handleChangeProductName,
      handleSelect,
      isCreate: !parentProductId && !parsedForm?.productName,
      isEdit: !!parsedForm?.productName,
      showSearch: isNewProduct,
      handleChangePF,
    });
  }, [
    brandOptions,
    processingFacilityOptions,
    fieldsMap,
    isDisabled,
    isNewProduct,
    products,
  ]);

  useEffect(() => {
    if (!!parsedForm?.image) {
      setImage(parsedForm.image);
      setFileList([{ ...defaultFileProps, url: parsedForm.image }]);
    }
  }, [parsedForm?.image]);

  const handleRedirectToPf = (pfId: string) => () =>
    redirectToActivityDataEntry(pfId, { activeTab: defaultYearTab });

  const noActiveYears = () => {
    setIsValidationVisible(true);
    return [];
  };

  const activeYears: number[] = useMemo(() => {
    if (location?.locationName && !location?.yearsWithScopeStatus?.length)
      return noActiveYears();

    const years = location?.yearsWithScopeStatus?.reduce(
      (acc: number[], item) => {
        if (item.isActive && item.status && item.year) {
          acc.push(Number(item.year));
        }
        return acc;
      },
      []
    );
    if (!years?.length && location?.locationName) return noActiveYears();

    return years ?? [];
  }, [location]);

  return (
    <>
      <Modal
        destroyOnClose={true}
        maskClosable={false}
        centered
        closeIcon={modalCloseIcon}
        width={CreateProductModalProps.MODAL_WIDTH}
        footer={null}
        wrapClassName={styles.modalContainer}
        open={showModalState}
        onOk={handleModalConfirm}
        onCancel={handleModalCancel}>
        <div className={styles.formContainer}>
          <div className={styles.headerContainer}>
            <h2>
              {isDisabled
                ? CreateProductModalProps.HEADING
                : parsedForm?.id
                ? CreateProductModalProps.UPDATE_HEADING
                : heading ?? CreateProductModalProps.CREATE_HEADING}
            </h2>
          </div>
          <Spin
            spinning={loading || productLoading || locationLoading}
            tip={productLoading && FETCH_NEW_PRODUCT_LOADER}>
            <ProductModalForm
              initialValues={initialFormValues}
              handleFormSubmission={handleFormSubmissionWrapper}
              validationSchema={validationSchema}
              validate={validateProductModal}
              image={image}
              fileList={fileList}
              setImage={setImage}
              isDisabled={isDisabled}
              fields={fields}
              processingFacilityFields={processingFacilityFields}
              activeYears={activeYears}
              buttonLoading={buttonLoading}
              parsedForm={parsedForm}
              handleModalCancel={handleModalCancel}
              createBrandModalState={createBrandModalState}
              handleCloseCreateBrandModal={handleCloseCreateBrandModal}
              fetchBrands={fetchBrands}
              createBrand={createBrand}
              isValidationVisible={isValidationVisible}
              toggleValidationModal={toggleValidationModal}
              handleRedirectToPf={handleRedirectToPf}
            />
          </Spin>
        </div>
      </Modal>
    </>
  );
};

export default CreateProductModal;
